import { useCallback, useMemo, useRef } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { RowSelectionState } from '@tanstack/react-table';

export interface RowSelectionController {
  allVisibleSelected: boolean;
  someVisibleSelected: boolean;
  selectedVisibleCount: number;
  toggleAllVisible: (checked: boolean) => void;
  toggleRow: (rowId: string, checked: boolean, shiftKey?: boolean) => void;
}

interface UseRowSelectionOptions {
  visibleRowIds: string[];
  rowSelection: RowSelectionState;
  setRowSelection: Dispatch<SetStateAction<RowSelectionState>>;
}

/**
 * Shared client-side row selection semantics for list and roster surfaces.
 *
 * Selection is keyed by stable row IDs. Select-all affects every row in the
 * current filtered result (not just the current pagination page), while a
 * shift-click applies the new checked state to the inclusive range between
 * the previous anchor and the clicked row.
 */
export function useRowSelection({
  visibleRowIds,
  rowSelection,
  setRowSelection,
}: UseRowSelectionOptions): RowSelectionController {
  const anchorRowId = useRef<string | null>(null);

  const selectedVisibleCount = useMemo(
    () => visibleRowIds.reduce((count, id) => count + (rowSelection[id] ? 1 : 0), 0),
    [rowSelection, visibleRowIds]
  );

  const allVisibleSelected =
    visibleRowIds.length > 0 && selectedVisibleCount === visibleRowIds.length;
  const someVisibleSelected =
    selectedVisibleCount > 0 && selectedVisibleCount < visibleRowIds.length;

  const toggleAllVisible = useCallback(
    (checked: boolean) => {
      setRowSelection((previous) => {
        const next = { ...previous };

        for (const rowId of visibleRowIds) {
          if (checked) next[rowId] = true;
          else delete next[rowId];
        }

        return next;
      });
      anchorRowId.current = null;
    },
    [setRowSelection, visibleRowIds]
  );

  const toggleRow = useCallback(
    (rowId: string, checked: boolean, shiftKey = false) => {
      const anchorIndex = anchorRowId.current
        ? visibleRowIds.indexOf(anchorRowId.current)
        : -1;
      const rowIndex = visibleRowIds.indexOf(rowId);
      const idsToChange =
        shiftKey && anchorIndex >= 0 && rowIndex >= 0
          ? visibleRowIds.slice(
              Math.min(anchorIndex, rowIndex),
              Math.max(anchorIndex, rowIndex) + 1
            )
          : [rowId];

      setRowSelection((previous) => {
        const next = { ...previous };

        for (const id of idsToChange) {
          if (checked) next[id] = true;
          else delete next[id];
        }

        return next;
      });
      anchorRowId.current = rowId;
    },
    [setRowSelection, visibleRowIds]
  );

  return {
    allVisibleSelected,
    someVisibleSelected,
    selectedVisibleCount,
    toggleAllVisible,
    toggleRow,
  };
}
